import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { publicApi, Move, Demonstration, MoveBreakdown, getMediaUrl } from '../services/api';
import BreakdownRows from '../components/BreakdownRows';
import VideoThumbnail from '../components/VideoThumbnail';
import { SkeletonDetail } from '../components/Skeleton';
import VideoModal from '../components/VideoModal';
import MediaDialog, { ShortcutHint } from '../components/MediaDialog';
import FeedbackThread from '../components/FeedbackThread';
import VideoPlayer from '../components/VideoPlayer';
import DemonstrationCard from '../components/DemonstrationCard';
import DemonstrationUploadModal from '../components/DemonstrationUploadModal';
import { useAuth } from '../context/AuthContext';
import { useMembership } from '../hooks/useMembership';
import PlanRequired from '../components/PlanRequired';

const MovePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const hasLibraryAccess = !!user?.role;
  const { needsPlan } = useMembership();

  const [isPreviewMove, setIsPreviewMove] = useState(false);
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);

  const [move, setMove] = useState<Move | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [variationsExpanded, setVariationsExpanded] = useState(sectionParam === 'variations');
  const [demosExpanded, setDemosExpanded] = useState(sectionParam === 'demonstrations');
  const [demonstrations, setDemonstrations] = useState<Demonstration[]>([]);
  const [demosLoading, setDemosLoading] = useState(false);
  const [demosFetched, setDemosFetched] = useState(false);

  const [breakdownsExpanded, setBreakdownsExpanded] = useState(sectionParam === 'breakdowns');
  const [breakdowns, setBreakdowns] = useState<MoveBreakdown[]>([]);
  const [breakdownsLoading, setBreakdownsLoading] = useState(false);
  const [breakdownsFetched, setBreakdownsFetched] = useState(false);
  const [selectedBreakdown, setSelectedBreakdown] = useState<MoveBreakdown | null>(null);

  const [selectedMove, setSelectedMove] = useState<Move | null>(null);
  const [selectedDemonstration, setSelectedDemonstration] = useState<Demonstration | null>(null);

  const variationsRef = useRef<HTMLDivElement>(null);
  const breakdownsRef = useRef<HTMLDivElement>(null);
  const demonstrationsRef = useRef<HTMLDivElement>(null);

  // Load move data and reset demonstration state on move change
  useEffect(() => {
    if (!id) return;

    // Reset state for the new move
    setDemonstrations([]);
    setDemosFetched(false);
    setDemosExpanded(sectionParam === 'demonstrations');
    setVariationsExpanded(sectionParam === 'variations');
    setBreakdowns([]);
    setBreakdownsFetched(false);
    setBreakdownsExpanded(sectionParam === 'breakdowns');

    const loadMove = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await publicApi.moves.getById(id);
        setMove(data);
      } catch (err) {
        setError('Failed to load move details');
        console.error('Error loading move:', err);
      } finally {
        setLoading(false);
      }
    };

    loadMove();
  }, [id, sectionParam]);

  // Detect if this is the featured "preview" move for anonymous users
  useEffect(() => {
    if (hasLibraryAccess || !id) {
      setIsPreviewMove(false);
      setPreviewVideoUrl(null);
      return;
    }
    publicApi.demonstrations.getFeatured().then((featured) => {
      if (featured && String(featured.moveId) === String(id)) {
        setIsPreviewMove(true);
        setPreviewVideoUrl(featured.videoUrl);
      } else {
        setIsPreviewMove(false);
        setPreviewVideoUrl(null);
      }
    }).catch(() => {
      setIsPreviewMove(false);
      setPreviewVideoUrl(null);
    });
  }, [id, hasLibraryAccess]);

  // Auto-fetch demonstrations if section=demonstrations
  useEffect(() => {
    if (sectionParam === 'demonstrations' && id && !demosFetched) {
      fetchDemonstrations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionParam, id, demosFetched]);

  // Auto-scroll to expanded section after data loads
  useEffect(() => {
    if (!loading && move) {
      if (sectionParam === 'variations' && variationsRef.current) {
        setTimeout(() => variationsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      } else if (sectionParam === 'breakdowns' && breakdownsRef.current && breakdownsFetched) {
        setTimeout(() => breakdownsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      } else if (sectionParam === 'demonstrations' && demonstrationsRef.current && demosFetched) {
        setTimeout(() => demonstrationsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  }, [loading, move, sectionParam, demosFetched]);

  const fetchDemonstrations = async () => {
    if (!id || demosFetched) return;
    setDemosLoading(true);
    try {
      const demos = await publicApi.demonstrations.getByMove(id);
      setDemonstrations(demos);
      setDemosFetched(true);
      setDemosExpanded(true);
    } catch (err) {
      console.error('Failed to fetch demonstrations:', err);
    } finally {
      setDemosLoading(false);
    }
  };

  // Fetch everything once — grouping/filtering by role happens client-side
  // in BreakdownRows (a move has few clips; refetching per role was wasteful).
  const fetchBreakdowns = async () => {
    if (!id) return;
    setBreakdownsLoading(true);
    try {
      const data = await publicApi.breakdowns.getByMove(id);
      setBreakdowns(data);
      setBreakdownsFetched(true);
      setBreakdownsExpanded(true);
    } catch (err) {
      console.error('Failed to fetch breakdowns:', err);
    } finally {
      setBreakdownsLoading(false);
    }
  };

  // Auto-fetch breakdowns if section=breakdowns
  useEffect(() => {
    if (sectionParam === 'breakdowns' && id && !breakdownsFetched) {
      fetchBreakdowns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionParam, id, breakdownsFetched]);

  const handleToggleBreakdowns = () => {
    if (!breakdownsExpanded && !breakdownsFetched) {
      fetchBreakdowns();
    } else {
      setBreakdownsExpanded(prev => !prev);
    }
  };

  const handleToggleVariations = () => {
    setVariationsExpanded(prev => !prev);
  };

  const handleToggleDemonstrations = () => {
    if (!demosExpanded && !demosFetched) {
      fetchDemonstrations();
    } else {
      setDemosExpanded(prev => !prev);
    }
  };

  const handleSelectMove = useCallback((m: Move) => {
    setSelectedMove(m);
  }, []);

  const handleSelectDemonstration = useCallback((demo: Demonstration) => {
    setSelectedDemonstration(demo);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedMove(null);
    setSelectedDemonstration(null);
    setSelectedBreakdown(null);
  }, []);

  if (loading) {
    return (
      <div className="move-page">
        <SkeletonDetail />
      </div>
    );
  }

  if (error || !move) {
    return (
      <div className="move-page">
        <Link to="/library/moves" className="move-page-back">&larr; Back to Syllabus</Link>
        <div className="error">{error || 'Move not found'}</div>
      </div>
    );
  }

  const defaultVideo = move.videos?.find(v => v.isDefault) || move.videos?.[0];
  const thumbnailUrl = defaultVideo?.thumbnail || move.thumbnail;
  const rawVideoUrl = defaultVideo?.videoUrl || move.videoUrl;
  // For anonymous preview move, use the featured demo video URL since backend strips move video
  const videoUrl = (!hasLibraryAccess && isPreviewMove && previewVideoUrl) ? previewVideoUrl : rawVideoUrl;
  // Signed in, but the studio's plans gate this move (members-only content)
  const locked = hasLibraryAccess && !!move.locked;

  return (
    <div className="move-page">
      <Link to="/library/moves" className="move-page-back">&larr; Back to Syllabus</Link>

      {/* Header: Video + Info */}
      <div className="move-page-header">
        <div className="move-page-video">
          {locked ? (
            <div className="locked-video">
              <span className="locked-video-badge">Members</span>
              <VideoThumbnail
                videoUrl=""
                thumbnailUrl={thumbnailUrl}
                alt={`${move.title} thumbnail`}
                onClick={() => navigate('/join')}
              />
            </div>
          ) : videoUrl && (hasLibraryAccess || isPreviewMove) ? (
            // Inline practice player — slow-mo, mirror and section loop right on the page
            <VideoPlayer
              src={getMediaUrl(videoUrl)}
              poster={thumbnailUrl ? getMediaUrl(thumbnailUrl) : undefined}
              title={move.title}
              loop
            />
          ) : (
            <VideoThumbnail
              videoUrl={videoUrl || ''}
              thumbnailUrl={thumbnailUrl}
              alt={`${move.title} thumbnail`}
              onClick={() => navigate(isAuthenticated ? '/signup' : `/login?returnUrl=${encodeURIComponent(`/library/moves/${id}`)}`)}
            />
          )}
        </div>
        <div className="move-page-info">
          <h1>{move.title}</h1>
          {move.variationDescription && (
            <span className="variation-badge">{move.variationDescription}</span>
          )}
          {move.description && <p className="description">{move.description}</p>}
          <div className="move-meta">
            {move.style && <span className="style">{move.style.name}</span>}
            {move.skillLevel && <span className="level">{move.skillLevel.name}</span>}
            {move.instructor && <span className="instructor">by {move.instructor.name}</span>}
            {move.moveType && <span>{move.moveType.name}</span>}
          </div>
          {(defaultVideo?.duration || move.duration) && (
            <div className="duration">
              Duration: {Math.floor((defaultVideo?.duration || move.duration!) / 60)}:{((defaultVideo?.duration || move.duration!) % 60).toString().padStart(2, '0')}
            </div>
          )}
          <span className="move-type-label">
            {move.isBaseMove ? 'Base Move' : 'Variation'}
          </span>
          {(!!user?.dancerId || !!user?.instructorId) && (
            needsPlan('feedback') ? (
              <Link to="/join" className="move-demonstrate-btn">Join to get feedback</Link>
            ) : (
              <button
                onClick={() => setShowUploadModal(true)}
                className="move-demonstrate-btn"
              >
                Demonstrate This Move
              </button>
            )
          )}
        </div>
      </div>

      {/* Variation of parent link */}
      {!move.isBaseMove && move.parentMove && (
        <div className="move-page-parent">
          Variation of: <Link to={`/library/moves/${move.parentMove.id}`}>{move.parentMove.title}</Link>
        </div>
      )}


      {locked && (
        <div className="move-page-section">
          <PlanRequired perk="library" />
        </div>
      )}

      {/* Breakdowns section */}
      <div className="move-page-section" ref={breakdownsRef}>
        {locked ? null : !hasLibraryAccess ? (
          <div className="gated-section">
            {isAuthenticated ? (
              <p>Your membership is pending confirmation. You'll be able to view breakdowns once approved.</p>
            ) : (
              <>
                <p>Log in to view breakdowns for this move.</p>
                <Link to={`/login?returnUrl=${encodeURIComponent(`/library/moves/${id}`)}`} className="btn btn-primary">Log in to view breakdowns</Link>
              </>
            )}
          </div>
        ) : (
          <>
            <button className="breakdowns-toggle" onClick={handleToggleBreakdowns}>
              {breakdownsLoading && !breakdownsExpanded ? 'Loading...' : breakdownsExpanded ? 'Hide breakdowns' : 'Show breakdowns'}
            </button>
            {breakdownsExpanded && (
              <div className="breakdowns-container">
                <h4>Move Breakdown:</h4>
                {breakdowns.length > 0 ? (
                  <BreakdownRows breakdowns={breakdowns} onPlay={setSelectedBreakdown} />
                ) : (
                  <div className="no-breakdowns">
                    <p>No breakdowns available yet.</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Variations section */}
      {move.variations && move.variations.length > 0 && (
        <div className="move-page-section" ref={variationsRef}>
          {!hasLibraryAccess ? (
            <div className="gated-section">
              <p>{move.variations.length} variation{move.variations.length !== 1 ? 's' : ''} available.</p>
              {isAuthenticated ? (
                <p>Your membership is pending confirmation.</p>
              ) : (
                <Link to={`/login?returnUrl=${encodeURIComponent(`/library/moves/${id}`)}`} className="btn btn-primary">Log in to view variations</Link>
              )}
            </div>
          ) : (
            <>
              <button className="variations-toggle" onClick={handleToggleVariations}>
                {variationsExpanded
                  ? `Hide ${move.variations.length} variation${move.variations.length !== 1 ? 's' : ''}`
                  : `Show ${move.variations.length} variation${move.variations.length !== 1 ? 's' : ''}`
                }
              </button>
              {variationsExpanded && (
                <div className="variations-container">
                  <h4>Variations:</h4>
                  <div className="variations-grid">
                    {move.variations.map((variation) => (
                      <div key={variation.id} className="video-card variation-card">
                        <VideoThumbnail
                          videoUrl={(variation.videos?.find(v => v.isDefault) || variation.videos?.[0])?.videoUrl || variation.videoUrl || ''}
                          thumbnailUrl={(variation.videos?.find(v => v.isDefault) || variation.videos?.[0])?.thumbnail || variation.thumbnail}
                          alt={`${variation.title} thumbnail`}
                          onClick={() => (variation.locked ? navigate('/join') : handleSelectMove(variation))}
                        />
                        <div className="move-header">
                          <div className="move-title-container">
                            <span className="variation-indent" aria-hidden="true">&darr; </span>
                            <span className="move-type-icon" aria-hidden="true">&#128260;</span>
                            <h3>
                              <Link to={`/library/moves/${variation.id}`} className="move-title-link">
                                {variation.title}
                              </Link>
                            </h3>
                            {variation.variationDescription && (
                              <span className="variation-badge">{variation.variationDescription}</span>
                            )}
                          </div>
                        </div>
                        <p className="description">{variation.description}</p>
                        <div className="move-meta">
                          {variation.style && <span className="style">{variation.style.name}</span>}
                          {variation.skillLevel && <span className="level">{variation.skillLevel.name}</span>}
                          {variation.instructor && <span className="instructor">by {variation.instructor.name}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Appears in patterns — surfaces every published pattern that uses this move
          as one of its sequenced steps. Helps a student who's drilling the move
          see what combos to try it in. */}
      {move.patternMoves && move.patternMoves.length > 0 && (
        <div className="move-page-section">
          <h3>Appears in {move.patternMoves.length} pattern{move.patternMoves.length !== 1 ? 's' : ''}</h3>
          <div className="move-pattern-list">
            {move.patternMoves.map((pm) => {
              const totalMoves = pm.pattern._count?.moves;
              const positionLabel = totalMoves
                ? `Step ${pm.position + 1} of ${totalMoves}`
                : `Step ${pm.position + 1}`;
              return (
                <Link
                  key={pm.id}
                  to={`/library/patterns/${pm.pattern.slug || pm.pattern.id}`}
                  className="move-pattern-card"
                >
                  <div className="move-pattern-card-title">{pm.pattern.title}</div>
                  <div className="move-pattern-card-position">{positionLabel}</div>
                  <div className="move-pattern-card-meta">
                    {pm.pattern.style?.name && <span>{pm.pattern.style.name}</span>}
                    {pm.pattern.skillLevel?.name && <span>· {pm.pattern.skillLevel.name}</span>}
                    {pm.pattern.instructor?.name && <span>· {pm.pattern.instructor.name}</span>}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Demonstrations section */}
      <div className="move-page-section" ref={demonstrationsRef}>
        {!hasLibraryAccess && !isPreviewMove ? (
          <div className="gated-section">
            {move._count?.demonstrations != null && (
              <p>{move._count.demonstrations} demonstration{move._count.demonstrations !== 1 ? 's' : ''} available.</p>
            )}
            {isAuthenticated ? (
              <p>Your membership is pending confirmation.</p>
            ) : (
              <Link to={`/login?returnUrl=${encodeURIComponent(`/library/moves/${id}`)}`} className="btn btn-primary">Log in to view demonstrations</Link>
            )}
          </div>
        ) : (
          <>
            <button className="demonstrations-toggle" onClick={handleToggleDemonstrations}>
              {demosLoading ? 'Loading...' : demosExpanded ? 'Hide demonstrations' : 'Show demonstrations'}
            </button>
            {demosExpanded && (
              <div className="demonstrations-container">
                <h4>Community Demonstrations:</h4>
                {demonstrations.length > 0 ? (
                  <div className="demonstrations-grid">
                    {demonstrations.map((demo) => (
                      <DemonstrationCard
                        key={demo.id}
                        demonstration={demo}
                        onSelect={handleSelectDemonstration}
                        showMove={false}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="no-demonstrations">
                    <p>No demonstrations yet. Be the first to demonstrate this move!</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Video Modal */}
      {selectedMove && (
        <VideoModal move={selectedMove} onClose={handleCloseModal} />
      )}

      {/* Breakdown Modal */}
      {selectedBreakdown && (
        <MediaDialog
          src={getMediaUrl(selectedBreakdown.videoUrl)}
          poster={selectedBreakdown.thumbnail ? getMediaUrl(selectedBreakdown.thumbnail) : undefined}
          title={selectedBreakdown.title}
          onClose={handleCloseModal}
        >
          <span className="media-dialog-eyebrow">Breakdown · {move?.title}</span>
          <h3>{selectedBreakdown.title}</h3>
          <div className="media-dialog-tags">
            <span>{selectedBreakdown.role.name}</span>
            <span>{selectedBreakdown.angle.name}</span>
            <span>{selectedBreakdown.clipType.name}</span>
          </div>
          {selectedBreakdown.counts && (
            <dl className="media-dialog-counts">
              <dt>Counts</dt>
              <dd>{selectedBreakdown.counts}</dd>
            </dl>
          )}
          {selectedBreakdown.description && <p>{selectedBreakdown.description}</p>}
          <ShortcutHint />
        </MediaDialog>
      )}

      {/* Demonstration Modal */}
      {selectedDemonstration && (
        <MediaDialog
          src={getMediaUrl(selectedDemonstration.videoUrl)}
          poster={selectedDemonstration.thumbnail ? getMediaUrl(selectedDemonstration.thumbnail) : undefined}
          title={`Demonstration by ${selectedDemonstration.dancer?.name || 'a dancer'}`}
          onClose={handleCloseModal}
        >
          <div className="media-dialog-person">
            {selectedDemonstration.dancer?.profileImage ? (
              <img
                src={selectedDemonstration.dancer.profileImage}
                alt=""
                className="media-dialog-avatar"
              />
            ) : (
              <span className="media-dialog-avatar" aria-hidden="true">
                {selectedDemonstration.dancer?.name?.charAt(0) || '?'}
              </span>
            )}
            <div>
              <strong>{selectedDemonstration.dancer?.name}</strong>
              <small>Demonstrating {move?.title}</small>
            </div>
          </div>
          {selectedDemonstration.caption && (
            <p className="media-dialog-quote">{selectedDemonstration.caption}</p>
          )}
          <FeedbackThread demonstration={selectedDemonstration} />
          <ShortcutHint />
        </MediaDialog>
      )}

      {/* Upload Demonstration Modal */}
      {showUploadModal && move && (
        <DemonstrationUploadModal
          move={move}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setDemosFetched(false);
            fetchDemonstrations();
          }}
        />
      )}
    </div>
  );
};

export default MovePage;
